import React, { memo, useCallback, useEffect } from 'react';
import { styled } from '@compiled/react';
import Button, { ButtonGroup } from '@atlaskit/button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import ModalDialog, {
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import log from '@atlassian/jira-common-util-logging/src/log';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	useDeleteAllDialogStatus,
	useDeleteAllService,
	useIssueAttachments,
} from '@atlassian/jira-issue-attachments-base';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { useDeletableAttachmentsCount } from '@atlassian/jira-issue-non-critical-gira-service';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';

const Dialog = () => {
	const issueKey = useIssueKey();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [
		{
			value: { totalCount: totalCountRaw, deletableCount: deletableCountRaw },
		},
		{ refreshAttachments },
	] = useIssueAttachments(issueKey);

	const [isDialogOpen, { setDeleteAllDialogOpen }] = useDeleteAllDialogStatus();
	const { showFlag } = useFlagsService();
	const [{ isDeleting, error: deleteError }, makeDeleteAllRequest] = useDeleteAllService(issueKey);

	let deletableCount = 0;
	if (ff('issue.details.gira-agg-migration-attachments_ecebe')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [{ value }] = useDeletableAttachmentsCount(issueKey);

		deletableCount = typeof value === 'number' ? value : 0;
	} else {
		// BE can temporarily return -1, so we safeguard it with `<= 0`
		deletableCount = typeof deletableCountRaw === 'number' ? deletableCountRaw : 0;
	}
	const totalCount = typeof totalCountRaw === 'number' ? totalCountRaw : 0;
	const canDeleteAll = deletableCount === totalCount;

	useEffect(() => {
		if (deleteError) {
			log.safeErrorWithoutCustomerData(
				'issue.attachments.delete-all',
				deleteError.message || deleteError.toString(),
			);

			fireErrorAnalytics({
				error: deleteError,
				meta: {
					id: 'deleteAllDialog',
					// eslint-disable-next-line jira/js/package-name-enforcer-for-error-analytics
					packageName: 'jiraIssueView',
					teamName: 'bento',
				},
			});

			showFlag({
				type: 'error',
				title: messages.deleteAllFailedNotificationTitle,
				description: [messages.deleteAllFailedNotificationDescription, { issueKey }],
				isAutoDismiss: true,
			});
		}
	}, [deleteError, issueKey, showFlag]);

	const onDelete = useCallback(() => {
		const didSucceed = makeDeleteAllRequest();
		const analyticsEvent = createAnalyticsEvent({
			action: 'submitted',
			actionSubject: 'form',
		});
		fireUIAnalytics(analyticsEvent, 'deleteAllAttachmentsDialog', {
			deletableCount,
		});
		// @ts-expect-error - TS2801 - This condition will always return true since this 'Promise<void>' is always defined.
		if (didSucceed) {
			refreshAttachments(issueKey);
		}
	}, [createAnalyticsEvent, deletableCount, issueKey, makeDeleteAllRequest, refreshAttachments]);

	const onCancel = useCallback(() => {
		setDeleteAllDialogOpen(false);
		const analyticsEvent = createAnalyticsEvent({
			action: 'closed',
			actionSubject: 'modalDialog',
		});
		fireUIAnalytics(analyticsEvent, 'deleteAllAttachmentsDialog', {
			deletableCount,
		});
	}, [createAnalyticsEvent, deletableCount, setDeleteAllDialogOpen]);

	const getMessage = useCallback(() => {
		if (canDeleteAll) {
			return deletableCount === 1
				? formatMessage(messages.deletingAllAttachmentsMessageSingle)
				: formatMessage(messages.deletingAllAttachmentsMessage, {
						deletableCount,
					});
		}
		return formatMessage(messages.deletingSomeAttachmentsMessage);
	}, [canDeleteAll, deletableCount, formatMessage]);

	if (typeof totalCount !== 'number' || deletableCount === 0 || !isDialogOpen) {
		return null;
	}

	return (
		<ShortcutScope>
			<ModalDialog width="small" onClose={onCancel}>
				<ModalHeader>
					<ModalTitle isMultiline={false} appearance="danger">
						{formatMessage(messages.heading)}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{!canDeleteAll && (
						<WarningContainer>
							<WarningIcon primaryColor={token('color.icon.warning', colors.Y500)} label="" />
							<Box xcss={warningMessageStyles}>
								{formatMessage(messages.deleteDetailsMessage, {
									nonDeletableCount: totalCount - deletableCount,
								})}
							</Box>
						</WarningContainer>
					)}
					<p>{getMessage()}</p>
				</ModalBody>
				<ModalFooter>
					<ButtonGroup>
						<Button
							onClick={onDelete}
							isDisabled={isDeleting}
							appearance="danger"
							interactionName="auto-generated.jira-issue-view-base.content.attachment.delete-all-dialog.button"
						>
							{isDeleting
								? formatMessage(messages.deletingButton)
								: formatMessage(messages.deleteButton)}
						</Button>
						<Button
							onClick={onCancel}
							appearance="subtle"
							interactionName="auto-generated.jira-issue-view-base.content.attachment.delete-all-dialog.button1"
						>
							{formatMessage(messages.cancelButton)}
						</Button>
					</ButtonGroup>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default memo<Record<any, any>>(Dialog);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WarningContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.warning', colors.Y50),
	display: 'flex',
	marginBottom: token('space.200', '16px'),
	padding: token('space.100', '8px'),
});

const warningMessageStyles = xcss({
	paddingLeft: 'space.100',
});
